/** The planner-facing routing facade: snap a click to the legal network,
 * solve a leg between two snapped points. Lazily opens the state's
 * routing.bin (DLR2, ranged) + routing.snap.bin (DLSG) on first use, the
 * same lazy pattern as terrainSamplerFor. */

import { route as astarRoute, type RouteOptions } from "./astar";
import { blockCached, DLR2Graph, httpRangeSource, type RangeSource } from "./dlr2";
import { SnapGrid } from "./snapgrid";
import type { LonLat, PlanLeg } from "./types";
import { TILES, type ManifestLayer, type StateManifest } from "../catalog";

export interface SnappedPoint {
  /** Graph vertex id — the leg solver's endpoints. */
  id: number;
  /** The vertex's coordinate (the click, magnetized onto the network). */
  at: LonLat;
}

export interface SolvedLeg {
  /** Geometry BETWEEN the endpoints, exclusive of both. */
  via: LonLat[];
  meters: number;
  /** seasons.json ids (0-based) of seasonally-restricted ways this leg
   * traverses — see PlanLeg.closureIds' doc comment in types.ts for the
   * +1/-1 storage convention. Always an array (never undefined); empty
   * when the route crosses no closed ways. Callers decide whether to keep
   * an empty array or drop the key entirely (solveLeg below drops it). */
  closureIds: number[];
  /** The dominant (most-traveled) way's name along the solved path, or
   * null when that way carries no name — see routerWith.solve below for
   * how "dominant" is weighted. Always present (never undefined), same
   * "always real, caller decides what to drop" shape as closureIds;
   * solveLeg below omits PlanLeg's name key entirely when this is null. */
  name: string | null;
}

export interface PlanRouter {
  /** Nearest network vertex within tolerance, or null (leave the point
   * where the user put it; the leg falls back to straight). */
  snap(at: LonLat): Promise<SnappedPoint | null>;
  /** Legal-network path between two snapped vertices, or null when
   * disconnected / over budget (caller renders an honest straight leg).
   * `opts` passes straight through to astar's search (abort signal, wall-
   * clock deadline, expansion cap) — see RouteOptions. */
  solve(fromId: number, toId: number, opts?: RouteOptions): Promise<SolvedLeg | null>;
}

/** package.py merges a "graphVersion" key into the manifest's "routing"
 * layer entry (sourced from routing.meta.json — see graph.py's build()),
 * the same pattern as dem_stream's terrain.meta.json "encoding" key. Not
 * on the shared ManifestLayer type since only this one layer carries it. */
interface RoutingManifestLayer extends ManifestLayer {
  graphVersion?: number;
}

/** Drives snap+solve over already-open graph/grid. This is the facade's
 * testable core — tests build graph/grid over memorySource and exercise
 * it directly; planRouterFor below is just the production wiring
 * (lazy HTTP-ranged open) on top of it. */
export function routerWith(graph: DLR2Graph, grid: SnapGrid): PlanRouter {
  return {
    async snap(at: LonLat): Promise<SnappedPoint | null> {
      const hit = await grid.nearest(at);
      return hit ? { id: hit.id, at: hit.at } : null;
    },
    async solve(fromId: number, toId: number, opts?: RouteOptions): Promise<SolvedLeg | null> {
      const result = await astarRoute(graph, fromId, toId, opts);
      if (!result) return null;
      // Every traversed way maps to its seasonClosureId (0 = none, else
      // seasons.json id + 1 — see types.ts's PlanLeg.closureIds comment
      // for why the -1 lives here at solve time). A Set dedupes the
      // common case of several consecutive edges sharing one way/closure;
      // sorted ascending so the field is stable for saved-plan diffing.
      //
      // Same loop also tallies the DOMINANT way for the name suggestion:
      // weighted by edge COUNT per way, not meters — RouteResult exposes
      // only the path's total meters, not a per-edge breakdown, so a true
      // meters-weighted majority isn't available here without re-deriving
      // per-edge distance from coords (which wouldn't even match the
      // graph's own edge lengths exactly). Edge count is a fine proxy in
      // practice (ways are subdivided into many short edges at similar
      // granularity) and is free to compute in this same pass. Map
      // iteration order is insertion order, so a tie keeps whichever way
      // was FIRST reached along the path — deterministic, and as good a
      // tie-break as any other for a save-time name suggestion.
      const closureIds = new Set<number>();
      const wayEdgeCounts = new Map<number, number>();
      for (const wayId of result.wayIds) {
        const closure = graph.way(wayId).seasonClosureId;
        if (closure > 0) closureIds.add(closure - 1);
        wayEdgeCounts.set(wayId, (wayEdgeCounts.get(wayId) ?? 0) + 1);
      }
      let dominantWayId: number | null = null;
      let dominantEdges = 0;
      for (const [wayId, edges] of wayEdgeCounts) {
        if (edges > dominantEdges) {
          dominantWayId = wayId;
          dominantEdges = edges;
        }
      }
      const dominantName = dominantWayId !== null ? graph.wayName(dominantWayId) : "";
      return {
        via: result.coords.slice(1, -1),
        meters: result.meters,
        closureIds: [...closureIds].sort((x, y) => x - y),
        name: dominantName === "" ? null : dominantName,
      };
    },
  };
}

function openSource(url: string): RangeSource {
  return blockCached(httpRangeSource(url));
}

/** null when the state serves no graph or no snap grid. */
export function planRouterFor(manifest: StateManifest): PlanRouter | null {
  const routingLayer = manifest.layers.routing as RoutingManifestLayer | undefined;
  const snapLayer = manifest.snapgrid;
  if (routingLayer?.graphVersion !== 2 || !snapLayer) return null;

  // Pull the bits we need into plain locals right away so the lazy
  // open() closure below doesn't have to re-narrow the optional fields.
  const region = manifest.region;
  const routingFile = routingLayer.file;
  const snapFile = snapLayer.file;

  let inner: Promise<PlanRouter> | null = null;
  async function open(): Promise<PlanRouter> {
    const [graph, grid] = await Promise.all([
      DLR2Graph.open(openSource(`${TILES}/${region}/${routingFile}`)),
      SnapGrid.open(openSource(`${TILES}/${region}/${snapFile}`)),
    ]);
    return routerWith(graph, grid);
  }

  return {
    async snap(at: LonLat): Promise<SnappedPoint | null> {
      if (!inner) inner = open();
      return (await inner).snap(at);
    },
    async solve(fromId: number, toId: number, opts?: RouteOptions): Promise<SolvedLeg | null> {
      if (!inner) inner = open();
      return (await inner).solve(fromId, toId, opts);
    },
  };
}

/** How long the FAST, honest answer may take before solveLeg hands back a
 * straight leg instead of waiting further — the engine's own budget
 * (astar.ts's maxExpansions/deadlineMs) is a second backstop against
 * runaway wall time, but a slow ranged fetch (cold cache, poor
 * connection) can stall well before expansions alone would trip it. */
const SOLVE_TIMEOUT_MS = 12_000;

/** Ceiling for the PATIENT continuation solveLeg keeps running in the
 * background past SOLVE_TIMEOUT_MS. Legs longer than ~25 mi routinely
 * need more than 12 s of real search time on a cold cache; without this,
 * every long leg would degrade to a straight line and stay that way. One
 * search runs once against this budget — there is no restart. */
const SOLVE_TOTAL_TIMEOUT_MS = 120_000;
const SOLVE_TOTAL_MAX_EXPANSIONS = 2_000_000;

/** Races `p` against `ms` — resolves with `p`'s value if it lands first,
 * else `null`. Never rejects: a network error mid-solve degrades the same
 * way a timeout does, so callers don't need two failure paths to reason
 * about. solveLeg below uses this for its 12 s honest-answer race only;
 * `p` itself is left running regardless of which way the race goes, since
 * solveLeg keeps its own reference to it for the late-upgrade path. */
function withTimeout<T>(p: Promise<T>, ms: number): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms);
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      () => {
        clearTimeout(timer);
        resolve(null);
      },
    );
  });
}

/** The leg-solving contract the draw-mode "solve every straight leg"
 * effect drives: snap both endpoints, route between the hits, and always
 * resolve to a definitive leg — never throws, never hangs past
 * SOLVE_TIMEOUT_MS. Two-phase, because "honest" and "patient" pull in
 * opposite directions: the UI needs an answer within 12 s to stay
 * responsive, but a 25+ mi leg on a cold cache can genuinely need much
 * longer to find a real path. So exactly ONE underlying search is started
 * with the full SOLVE_TOTAL_TIMEOUT_MS/SOLVE_TOTAL_MAX_EXPANSIONS budget
 * (no restart — it's the same search, just raced against two clocks). If
 * it lands within 12 s, that result is returned directly, same as before
 * this two-phase split existed. Past 12 s, the caller gets an honest
 * straight leg right away, but the search keeps running unobserved; if it
 * later lands a SNAPPED leg and the caller hasn't aborted in the
 * meantime, `opts.onUpgrade` fires with the better leg. A late straight
 * or null result, or any error, is final silence — no upgrade, no throw;
 * the promise solveLeg returns never rejects, same as always. App's
 * applyLegSolution geometry guard (checking the upgrade's endpoints still
 * match the leg on screen before applying it) is what makes accepting a
 * late upgrade safe — a continuation for an edit the user has since
 * undone just becomes a no-op instead of a surprise. No vertex-id
 * bookkeeping: `a`/`b` are plan coordinates (a snapped point's stored
 * coords ARE its vertex's coords, per the planner's coords-only
 * contract), so re-snapping them here to recover the vertex ids
 * astar.route needs is exact and cheap — 2-3 ranged reads apiece, well
 * inside the timeout on a warm graph. Any miss (either end doesn't snap)
 * or disconnect (solve finds no path) degrades to an honest straight leg
 * rather than a partial/wrong one, exactly as before.
 *
 * `opts.onSettled` fires exactly once per call, whenever the UNDERLYING
 * search (`full` below) itself finishes — snapped fast, straight fast, a
 * late upgrade, a late null, or an error — regardless of which arm of the
 * 12s race actually consumed the result and regardless of abort state.
 * It's attached directly to `full` before that race even starts, so it
 * can't double-fire and doesn't depend on onUpgrade or the fast path ever
 * running: callers that need to know "is a search for this key still
 * genuinely in flight" (as opposed to "is it within its first 12s") use
 * this instead of trying to infer it from the return value's timing. */
export async function solveLeg(
  router: PlanRouter,
  a: LonLat,
  b: LonLat,
  opts?: { signal?: AbortSignal; onUpgrade?: (leg: PlanLeg) => void; onSettled?: () => void },
): Promise<PlanLeg> {
  const full = solveLegNow(router, a, b, {
    signal: opts?.signal,
    deadlineMs: SOLVE_TOTAL_TIMEOUT_MS,
    maxExpansions: SOLVE_TOTAL_MAX_EXPANSIONS,
  });
  // See the doc comment above — a standalone subscription so it fires on
  // settlement no matter what the 12s race below does with the value.
  full.then(
    () => opts?.onSettled?.(),
    () => opts?.onSettled?.(),
  );

  const fast = await withTimeout(full, SOLVE_TIMEOUT_MS);
  if (fast) return fast;

  full.then(
    (leg) => {
      if (leg.mode === "snapped" && !opts?.signal?.aborted) opts?.onUpgrade?.(leg);
    },
    () => {},
  );
  return { mode: "straight", via: [] };
}

async function solveLegNow(router: PlanRouter, a: LonLat, b: LonLat, opts?: RouteOptions): Promise<PlanLeg> {
  const [fromHit, toHit] = await Promise.all([router.snap(a), router.snap(b)]);
  if (!fromHit || !toHit) return { mode: "straight", via: [] };
  const solved = await router.solve(fromHit.id, toHit.id, opts);
  if (!solved) return { mode: "straight", via: [] };
  return {
    mode: "snapped",
    via: solved.via,
    ...(solved.closureIds.length ? { closureIds: solved.closureIds } : {}),
    ...(solved.name !== null ? { name: solved.name } : {}),
  };
}
